import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { Sofa, Info, HelpCircle, LifeBuoy, Tag, LogIn, MapPin, Handshake, Gift, FileText, ArrowRight, Banknote } from "lucide-react";

const exploreLinks = [
  { icon: Banknote, label: "Guaranteed Rent", path: "/guaranteed-rent/" },
  { icon: Sofa, label: "Interiors & Staging", path: "/interiors" },
  { icon: Gift, label: "Referral Programme", path: "/referral" },
  { icon: Handshake, label: "Houst Partnership", path: "/resources/houst-partnership" },
];

const getStartedLinks = [
  { icon: Info, label: "About Us", path: "/about" },
  { icon: HelpCircle, label: "FAQs", path: "/faqs" },
  { icon: LifeBuoy, label: "Support", path: "/support" },
  { icon: LogIn, label: "Host Login", path: "/login" },
];

const colHeading = {
  fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em",
  textTransform: "uppercase", color: "#9A9A9A", marginBottom: 16, marginTop: 0,
};

const rowBase = {
  display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
  borderRadius: 8, textDecoration: "none", transition: "background 0.15s",
};

function RowLink({ icon: Icon, label, path, onClose }) {
  return (
    <Link to={path} onClick={onClose} style={rowBase}
      onMouseEnter={e => e.currentTarget.style.background = "#F7F7F5"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div className="mega-icon-box" style={{ width: 24, height: 24, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} color="#555" strokeWidth={1.8} />
      </div>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 14, color: "#1A1A1A", margin: 0, lineHeight: 1.2 }}>{label}</p>
    </Link>
  );
}

export default function MoreMegaMenu({ onClose, onMouseEnter, onMouseLeave }) {
  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, top: "calc(68px + var(--ts-promo-height, 0px))", background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.45) 100%)", zIndex: 150, animation: "fadeIn 0.2s ease" }}
        onMouseEnter={onMouseLeave}
      />
      <div style={{ position: "fixed", top: "calc(68px + var(--ts-promo-height, 0px))", left: 0, right: 0, zIndex: 300 }}
        onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className="more-mega-panel">
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 clamp(20px, 5vw, 80px)" }}>
          <div style={{ background: "#fff", borderRadius: "18px 18px 18px 18px", boxShadow: "0 20px 48px -12px rgba(0,0,0,0.22)", overflow: "hidden", animation: "megaSlideDown 0.25s cubic-bezier(0.16, 1, 0.3, 1)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.1fr", gap: 0 }} className="more-mega-grid">
              <div style={{ padding: "28px 32px 24px", paddingRight: 24, borderRight: "1px solid #F0F0F0" }}>
                <p style={colHeading}>Explore</p>
                <div className="mega-link-list" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {exploreLinks.map((l) => (<RowLink key={l.label} {...l} onClose={onClose} />))}
                </div>
              </div>
              <div style={{ padding: "28px 24px 24px 24px", borderRight: "1px solid #F0F0F0" }}>
                <p style={colHeading}>More</p>
                <div className="mega-link-list" style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {getStartedLinks.map((l) => (<RowLink key={l.label} {...l} onClose={onClose} />))}
                </div>
              </div>
              <div style={{ background: "#F4F2EE", padding: "28px 24px" }}>
                <p style={colHeading}>Featured</p>
                <Link to="/referral" onClick={onClose} style={{ display: "block", textDecoration: "none", transition: "transform 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <div style={{ width: "100%", aspectRatio: "16/10", borderRadius: 10, background: "#EBE9E4", marginBottom: 16, overflow: "hidden" }}>
                    <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/bbbb5766b_StreoAd-260x2003.jpg" alt="Refer a host" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
                  </div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A", margin: "0 0 6px" }}>Refer a host, earn £250</p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666", lineHeight: 1.5, margin: "0 0 14px" }}>Introduce a landlord and earn £250 cash or up to 10% of their fees.</p>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600, color: "#1A1A1A", display: "inline-flex", alignItems: "center", gap: 4 }}>Learn more <ArrowRight size={13} /></span>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .more-mega-grid { grid-template-columns: 1fr !important; }
            .more-mega-grid > div { border-right: none !important; padding-left: 0 !important; padding-right: 0 !important; padding-top: 20px !important; border-top: 1px solid #F0F0F0; }
            .more-mega-grid > div:first-child { border-top: none !important; padding-top: 0 !important; }
            .more-mega-grid > div:last-child { background: #F4F2EE !important; border-radius: 12px !important; padding: 24px !important; }
          }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .mega-link-list > a:hover .mega-icon-box svg { color: #1A1A1A !important; }
            @keyframes megaSlideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    </>,
    document.body
  );
}